import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { useProducts } from "../../lib/products";
import { Reveal, Eyebrow } from "./Reveal";
import { Confirmation } from "./Confirmation";
import { ProductPicker } from "./ProductPicker";

const FREQS = [["once", "One-off"], ["weekly", "Weekly"], ["fortnightly", "Fortnightly"]];
const empty = { name: "", email: "", phone: "", address: "", suburb: "", notes: "" };

const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="block">
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--sage)]">{label}</span>
    {children}
  </label>
);

const Freq = ({ value, set }) => (
  <div className="grid grid-cols-3 gap-1 rounded-2xl border border-white/10 bg-black/20 p-1">
    {FREQS.map(([v, l]) => (
      <button key={v} type="button" onClick={() => set(v)} className={`relative rounded-xl py-3 text-sm font-semibold transition-colors ${value === v ? "text-[#0F1410]" : "text-[var(--sage)] hover:text-white"}`} data-testid={`order-frequency-${v}`}>
        {value === v && <motion.span layoutId="freq-pill" className="absolute inset-0 rounded-xl bg-[#EAB308]" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
        <span className="relative">{l}</span>
      </button>
    ))}
  </div>
);

export const OrderSection = () => {
  const [freq, setFreq] = useState("once");
  const products = useProducts();
  const [cart, setCart] = useState({});
  const [notes, setNotes] = useState({});
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const items = products.filter((p) => cart[p.id] > 0).map((p) => ({ product_id: p.id, quantity: cart[p.id], note: p.note_prompt ? (notes[p.id] ?? "").trim() : "" }));
  const total = products.reduce((a, p) => a + (cart[p.id] ?? 0) * (p.price ?? 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) {
      setError("Add at least one product to your order.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post("/orders", { ...form, items, frequency: freq });
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = (k, props = {}) => (
    <input id={k} value={form[k]} onChange={on(k)} required className="field" data-testid={`order-input-${k}`} {...props} />
  );

  return (
    <section id="order" className="relative border-t border-[#1B2A1C]/10 bg-[#F3EEDF]" data-testid="order-section">
      <div className="mx-auto grid max-w-[1440px] gap-14 px-5 py-24 sm:px-10 md:py-32 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Order now</Eyebrow>
          <h2 className="mt-5 font-display text-6xl font-extrabold leading-[0.88] tracking-tight sm:text-8xl">
            Fill your box.<br /><span className="italic text-[var(--gold)]">Your way.</span>
          </h2>
          <p className="mt-8 max-w-md text-[var(--sage)] sm:text-lg">
            Pick what you'd like, fill in your details and you'll get an order reference straight away. Pay by online banking using that reference and we'll get packing.
          </p>
          <div className="mt-10 hidden overflow-hidden rounded-[28px] lg:block">
            <img src="/images/box-white.jpg" alt="Fresh vegetables packed in a delivery box" className="photo-bright h-72 w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="min-w-0 lg:col-span-7">
          <div className="rounded-[32px] border border-[#1B2A1C]/10 bg-white p-5 shadow-[0_30px_80px_-40px_rgba(27,42,28,0.35)] sm:p-10">
            <AnimatePresence initial={false}>
              {order ? (
                <Confirmation key="done" order={order} onNew={() => { setOrder(null); setForm(empty); setCart({}); setNotes({}); setFreq("once"); }} />
              ) : (
                <motion.form key="form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid min-w-0 grid-cols-1 gap-5 sm:grid-cols-2" data-testid="order-form">
                  <div className="min-w-0 sm:col-span-2">
                    <Field label="What would you like?" id="products"><ProductPicker products={products} cart={cart} setCart={setCart} notes={notes} setNotes={setNotes} /></Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="How often?" id="freq"><Freq value={freq} set={setFreq} /></Field>
                    {freq !== "once" && <p className="mt-2 text-xs text-[var(--sage)]" data-testid="order-frequency-note">We'll email you before each delivery with a reference to pay. Pause or cancel any time by email or phone.</p>}
                  </div>
                  <div className="sm:col-span-2 flex items-end justify-between border-y border-white/10 py-4">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--sage)]">{freq === "once" ? "Total" : "Per delivery"}</p>
                    <p className="font-display text-5xl font-extrabold text-[var(--gold)]" data-testid="order-total">${total.toFixed(2)}</p>
                  </div>
                  <Field label="Full name" id="name">{input("name", { autoComplete: "name" })}</Field>
                  <Field label="Phone" id="phone">{input("phone", { type: "tel", autoComplete: "tel" })}</Field>
                  <div className="sm:col-span-2"><Field label="Email" id="email">{input("email", { type: "email", autoComplete: "email" })}</Field></div>
                  <div className="sm:col-span-2"><Field label="Delivery address" id="address">{input("address", { autoComplete: "street-address" })}</Field></div>
                  <div className="sm:col-span-2"><Field label="Suburb" id="suburb">{input("suburb")}</Field></div>
                  <div className="sm:col-span-2">
                    <Field label="Notes (optional)" id="notes">
                      <textarea id="notes" rows={3} value={form.notes} onChange={on("notes")} className="field resize-none" placeholder="Gate code, leave by the door, allergies…" data-testid="order-input-notes" />
                    </Field>
                  </div>
                  {error && <p className="sm:col-span-2 rounded-xl bg-[#FEE4E2] px-4 py-3 text-sm text-[#B42318]" data-testid="order-error">{error}</p>}
                  <button type="submit" disabled={busy} className="btn-gold group sm:col-span-2 justify-center py-5 text-lg disabled:opacity-60" data-testid="order-form-submit-button">
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{freq === "once" ? "Place order" : "Start subscription"} <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></>}
                  </button>
                  <p className="sm:col-span-2 text-center text-xs text-[var(--sage)]">We'll confirm your delivery day by email.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
